/**
 * mcpbActions.js — pure derivation of the per-cell ••• menu + cell status (2c).
 *
 * Verified against the RENDERED mocks (02-unified-bar*.html). Every render except mobile-portrait shows
 * each cell with a surfaced top-right row [Snapshot][Download][Archive][Export][•••] where Archive/Export
 * fill (green/accent) as status. On mobile-portrait the tiles are too small for a 5-button row, so
 * (David 2026-06-19): the cell shows only small Archive + Export STATUS icons (shown when flagged) + a
 * per-cell ••• that carries the ACTIONS — Snapshot · Download · Archive (set/unset) · Export (set/unset)
 * · Bookmark (v2). Setting/clearing the flags happens inside the •••; the cell icons are status-only.
 *
 * This module owns the pure label/title/icon/active/disabled derivation (unit-testable). The View
 * attaches each item's onClick. Cell status icons are derived from the same fArchive/fExport booleans.
 *
 * Flag constants are VERIFIED against rda-backend/src/db/flags.py (FLAG_ARCHIVE=1, FLAG_EXPORT=2) and
 * the Java reference (EventFlags.ARCHIVE=1 / EXPORT=2; Event.java setEventFlag(sAuth, bID, flag, fEnable)).
 */

export const ARCHIVE_FLAG = 1;
export const EXPORT_FLAG = 2;

// Material-style 24x24 path data (inline so the menu stays one lightweight render).
const ICON = {
  snap: 'M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z',
  dl: 'M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z',
  archive: 'M20 6H4v2h16V6zM3 4h18v3H3V4zm17 6H4v10h16V10zM10 14v-2h4v2h-4z',
  export: 'M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z', // matches the mock's export glyph (up-arrow-out)
};

// The two cell status icons (archive/export) — shown on the cell only when the current clip carries
// that flag (mock: archived = green, exported = accent). Status-only; toggling is in the •••.
export const STATUS_ICONS = {
  archive: ICON.archive,
  export: ICON.export,
};

/**
 * Derive the per-cell ••• menu items for the clip currently under the playhead.
 * @param {object|null} activeEvent  the frozen event under the playhead (null in a gap)
 * @param {object} opts
 * @param {boolean} [opts.canExport]  the user holds the export right (state.user.fExport)
 * @returns {Array<{action,label,title,path,active?,disabled?,placeholder?}>}
 */
export const deriveActionItems = (activeEvent, { canExport = false, isWebCodecs = false } = {}) => {
  const hasClip = !!activeEvent;
  const archived = !!(activeEvent && activeEvent.fArchive);
  const exported = !!(activeEvent && activeEvent.fExport);

  // `primary: true` marks the SURFACED actions (#16) — the ones shown as the cell's quick-action
  // row. The View de-dups them OUT of the ••• menu when the "always-show quick actions" toggle is on
  // (they're already surfaced); with the toggle off they stay in the menu so they're reachable without
  // hovering. Bookmark (v2) is never primary — it lives only in the menu.
  //
  // Snapshot: BENCHED for eView/dView (cross-origin <video> taints canvas — #86baz05kd). Cloud/dragon
  // uses WebCodecs <canvas> which is same-origin, so snapshot works there. Bulk composite too.
  const snapItem = isWebCodecs
    ? { action: 'snap', label: 'Snapshot', title: 'Capture the current frame', path: ICON.snap, disabled: !hasClip, primary: true }
    : null;
  return [
    ...(snapItem ? [snapItem] : []),
    { action: 'dl', label: 'Download', title: 'Download this clip', path: ICON.dl, disabled: !hasClip, primary: true },
    {
      action: 'archive',
      label: archived ? 'Remove from archive' : 'Archive',
      title: archived ? 'Remove this clip from the archive' : 'Protect this clip from auto-deletion',
      path: ICON.archive,
      active: archived,
      disabled: !hasClip,
      primary: true,
    },
    {
      action: 'export',
      label: exported ? 'Cancel export' : 'Export',
      title: canExport
        ? (exported ? 'Cancel the export flag on this clip' : 'Flag this clip for export')
        : 'You do not have the export permission',
      path: ICON.export,
      active: exported,
      disabled: !hasClip || !canExport,
      primary: true,
    },
  ];
};

/**
 * Derive the BULK-actions menu items (conf>1) — "apply to all N visible cams" (§15 #23, verified vs the
 * rendered desktop bulk popover). Disabled when nothing is visible; Export-all also needs the export
 * right. Download-with-overlay variant + the "adjust which cams" picker are v2 (the header conveys the
 * default scope = all visible). The View wires onClick to iterate the visible cells.
 *
 * @param {object} opts
 * @param {number} [opts.visibleCount]  number of filled, visible cells the bulk action applies to
 * @param {boolean} [opts.canExport]
 */
export const deriveBulkItems = ({ visibleCount = 0, canExport = false, isWebCodecs = false } = {}) => {
  const none = visibleCount === 0;
  // Bulk composite snapshot: BENCHED for eView/dView (cross-origin taint — #86baz05kd). Cloud/dragon
  // uses WebCodecs <canvas> (same-origin), so composite works there.
  const snapAllItem = isWebCodecs
    ? { action: 'snap-all', label: 'Snapshot all', title: 'Composite snapshot of all visible cameras', path: ICON.snap, disabled: none }
    : null;
  return [
    ...(snapAllItem ? [snapAllItem] : []),
    { action: 'dl-all', label: 'Download all clips', title: 'Download the current clip from every visible cam', path: ICON.dl, disabled: none },
    { action: 'archive-all', label: 'Archive all', title: 'Archive the current clip on every visible cam', path: ICON.archive, disabled: none },
    {
      action: 'export-all',
      label: 'Export all',
      title: canExport ? 'Flag the current clip for export on every visible cam' : 'You do not have the export permission',
      path: ICON.export,
      disabled: none || !canExport,
    },
  ];
};
